import React, { useState, useEffect } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Table, TableBody, TableCell, TableHead, TableRow, Typography,
    Paper, Stack, Grid, CircularProgress, Card, IconButton, InputAdornment
} from '@mui/material';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Takkevariheader from './Takkevariheader';

const Takkevaricust = () => {

    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        email: '',
        address: '',
        city: '',
        state: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            const q = query(
                collection(db, 'takkevaricusts')
            );
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCustomers(data);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    const handleOpen = () => {
        setFormData({
            name: '',
            mobile: '',
            email: '',
            address: '',
            city: '',
            state: ''
        });
        setErrors({});
        setEditMode(false);
        setEditId(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setLoading(false);
        setErrors({});
    };

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    // ✅ Validation
    const validate = () => {
        let temp = {};
        temp.name = formData.name ? "" : "Name is required";
        temp.mobile = /^[0-9]{10}$/.test(formData.mobile) ? "" : "Enter valid 10-digit mobile number";

        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);

        try {
            const { name, mobile, email, address, city, state } = formData;
            const propid = auth.currentUser?.uid;

            if (editMode && editId) {
                await updateDoc(doc(db, 'takkevaricusts', editId), {
                     name, mobile, email, address, city, state
                });
                toast.success("Customer updated");
            } else {
                await addDoc(collection(db, 'takkevaricusts'), {
                     name, mobile, email, address, city, state, propid
                });
                toast.success("Customer added");
            }

            fetchCustomers();
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error("Error: " + err.message);
        }

        setLoading(false);
    };

    const handleEdit = (cust) => {
        setFormData({
            name: cust.name,
            mobile: cust.mobile,
            email: cust.email,
            address: cust.address,
            city: cust.city,
            state: cust.state
        });
        setEditId(cust.id);
        setEditMode(true);
        setOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, 'takkevaricusts', id));
            toast.success("Customer deleted");
            fetchCustomers();
        } catch (err) {
            console.error(err);
            toast.error("Error deleting customer");
        }
    };

    // ✅ Search filter
    const filtered = customers.filter(cust =>
        cust.name.toLowerCase().includes(search.toLowerCase()) ||
        cust.mobile.includes(search) ||
        cust.email.toLowerCase().includes(search.toLowerCase())
    );

    const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box>
            <Takkevariheader />
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Button variant="contained" onClick={handleOpen}>Add खातेदारांची माहिती </Button>
                <Typography variant="h5" fontWeight="bold">खातेदारांची माहिती </Typography>
            </Stack>


            {/* ✅ Search Box */}
            <TextField
                placeholder="Search by name, mobile, or email"
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 2 }}
            />

            <Card>
                <Paper>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                                <TableCell>#</TableCell>
                                <TableCell>Actions</TableCell>
                                <TableCell>खातेदाराचे  नाव </TableCell>
                                <TableCell>मोबाइल</TableCell>
                                <TableCell>ई-मेल </TableCell>
                                <TableCell>ऍड्रेस </TableCell>
                                <TableCell>सिटी </TableCell>
                                <TableCell>स्टेट </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">No data found</TableCell>
                                </TableRow>
                            ) : (
                                paginated.map((cust, index) => (
                                    <TableRow key={cust.id}>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <IconButton color="primary" onClick={() => handleEdit(cust)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDelete(cust.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{cust.name}</TableCell>
                                        <TableCell>{cust.mobile}</TableCell>
                                        <TableCell>{cust.email}</TableCell>
                                        <TableCell>{cust.address}</TableCell>
                                        <TableCell>{cust.city}</TableCell>
                                        <TableCell>{cust.state}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        rowsPerPageOptions={[10, 20, 30]}
                        component="div"
                        count={filtered.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle>{editMode ? 'Edit' : 'Add'} खातेदार</DialogTitle>
                <DialogContent dividers>
                    <div className='row'>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="खातेदाराचे  नाव"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="मोबाइल"
                                name="mobile"
                                value={formData.mobile}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.mobile}
                                helperText={errors.mobile}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="ई-मेल"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.email}
                                helperText={errors.email}
                            />
                        </Grid>
                        <Grid className="col-sm-12 mt-3">
                            <TextField
                                label="ऍड्रेस"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                fullWidth size="small"
                                multiline rows={2}
                                required
                                error={!!errors.address}
                                helperText={errors.address}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="सिटी"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.city}
                                helperText={errors.city}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="स्टेट"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.state}
                                helperText={errors.state}
                            />
                        </Grid>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="error">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                        sx={{ ml: 2 }} // small spacing between buttons
                    >
                        {loading ? <CircularProgress size={24} /> : (editMode ? "Update" : "Add")}
                    </Button>
                </DialogActions>

            </Dialog>
        </Box>
    );
};

export default Takkevaricust;
